import { useGetExecutionFeeUsd } from '@ui/features/Trade/hooks/useGetExecutionFee';
import { ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { OrderType } from '@ui/types/trade';
import { OpenPositionFee } from '@ui/utils/trade/getOpenPositionFee';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';

import { useCollateralToken } from '../store/useCollateralToken';
import { useOrderType } from '../store/useOrderType';
import { useEstOpenLimitGasFee } from './useEstOpenLimitGasFee';
import { useEstOpenPositionGasFee } from './useEstOpenPositionGasFee';
import { useIsConfirmBtnDisabled } from './useIsConfirmBtnDisabled';
import { useOpenPositionFee } from './useOpenPositionFee';

type TotalOpenPositionFee = {
  openPositionFee:
  | {
    value: OpenPositionFee;
    usd: OpenPositionFee;
  }
  | undefined;
  txnActionGasFee: BigNumber | undefined;
  executionFeeUsd: BigNumber | undefined;
  setState: (state: Omit<TotalOpenPositionFee, 'setState'>) => void;
};
const useTotalOpenPositionFeeStore = create<TotalOpenPositionFee>((set) => {
  return {
    openPositionFee: undefined,
    txnActionGasFee: undefined,
    executionFeeUsd: undefined,
    setState: (state) => set(state),
  };
});

export const useTotalOpenPositionFeeUpdate = () => {
  const openPositionFee = useOpenPositionFee();
  const executionFeeUsd = useGetExecutionFeeUsd();
  const orderType = useOrderType((s) => s._persistValue);
  const isMarket = orderType === OrderType.MARKET;
  const [isConfirmBtnDisabled] = useIsConfirmBtnDisabled();
  const openPositionFeeStore = useTotalOpenPositionFeeStore();

  const estimateOpenPositionGasFee = useEstOpenPositionGasFee({
    disabled: !!isConfirmBtnDisabled || !isMarket,
  });
  const estimateOpenLimitGasFee = useEstOpenLimitGasFee({
    disabled: !!isConfirmBtnDisabled || isMarket,
  });
  const txnActionGasFee = isMarket
    ? estimateOpenPositionGasFee?.gasInUsd
    : estimateOpenLimitGasFee?.gasInUsd;

  useEffect(() => {
    if (
      openPositionFeeStore.executionFeeUsd?.toString() ==
      executionFeeUsd?.toString() &&

      openPositionFeeStore.openPositionFee?.value.final.toString() ==
      openPositionFee?.value.final.toString() &&

      openPositionFeeStore.openPositionFee?.usd.final.toString() ==
      openPositionFee?.usd.final.toString() &&

      openPositionFeeStore.txnActionGasFee?.toString() ==
      txnActionGasFee?.toString()
    ) {
      return;
    }
    openPositionFeeStore.setState({
      openPositionFee,
      txnActionGasFee,
      executionFeeUsd,
    });
  }, [
    txnActionGasFee?.toString(),
    openPositionFee?.usd.final.toString(),
    openPositionFee?.value.final.toString(),
    executionFeeUsd?.toString()
  ]);
};

export const useTotalOpenPositionFee = ({
  isGasFeeIncluded = true,
}: {
  isGasFeeIncluded?: boolean;
}) => {
  const { openPositionFee, txnActionGasFee, executionFeeUsd } =
    useTotalOpenPositionFeeStore();
  if (isNil(executionFeeUsd)) return undefined;

  return {
    base: executionFeeUsd
      .plus(openPositionFee ? openPositionFee.usd.base : ZERO_BIG_NUMBER)
      .plus(
        isGasFeeIncluded ? txnActionGasFee ?? ZERO_BIG_NUMBER : ZERO_BIG_NUMBER,
      ),
    final: executionFeeUsd
      .plus(openPositionFee ? openPositionFee.usd.final : ZERO_BIG_NUMBER)
      .plus(
        isGasFeeIncluded ? txnActionGasFee ?? ZERO_BIG_NUMBER : ZERO_BIG_NUMBER,
      ),
  };
};